// The pieces that make a drawer do something, rather than only show
// something.
//
// One mutation path for the whole module. Every action goes through
// `useAction`, so every action gets the same three things without anyone
// remembering to add them: a button that disables itself while in flight, an
// error that says what failed and whether anything was written, and a
// refresh of the list behind the drawer on success.
//
// No page reloads and no window.confirm anywhere: a native confirm blocks
// the whole renderer, looks nothing like the product, and cannot say what
// the user is actually about to do.

import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { BASE_URL, C, Icons } from './kit';

// ─── The mutation hook ────────────────────────────────────────
//
// `run(config)` performs one write. `state` is what the UI needs to render
// the attempt honestly: idle / saving / failed, with the message.
export function useAction(onDone) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const run = useCallback(async ({ method = 'post', url, body, success }) => {
    setSaving(true);
    setError('');
    setNote('');
    try {
      const res = await axios({
        method, url: `${BASE_URL}${url}`, data: body, withCredentials: true,
      });
      if (success) setNote(typeof success === 'function' ? success(res.data) : success);
      if (onDone) await onDone(res.data?.data);
      return res.data?.data ?? true;
    } catch (e) {
      const status = e?.response?.status;
      // 409 is the server refusing on a rule, not failing — "already bound",
      // "issue the account first". That is an answer, and it should read
      // like one rather than like a crash.
      setError(
        status === 403 ? (e?.response?.data?.message || 'You do not have permission to do that.')
          : status === 409 ? (e?.response?.data?.message || 'That is not possible in the current state.')
            : (e?.response?.data?.message || e.message || 'Could not save. Nothing was changed.'),
      );
      return null;
    } finally {
      setSaving(false);
    }
  }, [onDone]);

  return { run, saving, error, note, clear: () => { setError(''); setNote(''); } };
}

// ─── Feedback ─────────────────────────────────────────────────
export function Notice({ error, note }) {
  if (!error && !note) return null;
  const bad = Boolean(error);
  return (
    <div style={{
      border: `1px solid ${bad ? '#fecaca' : '#bbf7d0'}`,
      background: bad ? '#fff7f7' : '#f0fdf4',
      color: bad ? C.red : C.green,
      borderRadius: 8, padding: '9px 11px', fontSize: 12.5, lineHeight: 1.5,
    }}>
      {error || note}
    </div>
  );
}

// ─── Action buttons ───────────────────────────────────────────
export function ActionButton({ children, onClick, tone = 'primary', disabled, busy, icon }) {
  const tint = {
    primary: { bg: C.burgundy, fg: '#fff', border: C.burgundy },
    quiet:   { bg: '#fff', fg: C.body, border: C.border },
    danger:  { bg: '#fff', fg: C.red, border: '#fecaca' },
  }[tone] || { bg: C.burgundy, fg: '#fff', border: C.burgundy };
  const off = disabled || busy;
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={off}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '7px 13px', borderRadius: 8,
        border: `1px solid ${tint.border}`, background: tint.bg, color: tint.fg,
        fontSize: 12.5, fontWeight: 600, cursor: off ? 'default' : 'pointer',
        opacity: off ? 0.55 : 1, fontFamily: 'inherit', whiteSpace: 'nowrap',
      }}
    >
      {busy ? '…' : icon}
      {children}
    </button>
  );
}

export function ActionRow({ children }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{children}</div>;
}

// ─── Confirm ──────────────────────────────────────────────────
// Inline, in the panel, naming the thing that is about to happen. Replaces
// the browser confirm, which cannot name it and freezes everything behind it.
export function ConfirmAction({ label, question, confirmLabel, onConfirm, tone = 'primary', busy }) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return <ActionButton tone={tone} busy={busy} onClick={() => setAsking(true)}>{label}</ActionButton>;
  }
  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px',
      background: C.panel, display: 'grid', gap: 9, width: '100%',
    }}>
      <div style={{ fontSize: 12.5, color: C.body, lineHeight: 1.5 }}>{question}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <ActionButton
          tone={tone}
          busy={busy}
          onClick={async () => { await onConfirm(); setAsking(false); }}
        >
          {confirmLabel || label}
        </ActionButton>
        <ActionButton tone='quiet' onClick={() => setAsking(false)}>Cancel</ActionButton>
      </div>
    </div>
  );
}

// ─── Inline fields ────────────────────────────────────────────
const labelStyle = { display: 'block', fontSize: 11.5, fontWeight: 600, color: C.muted, marginBottom: 4 };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px',
  border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 12.5,
  color: C.ink, background: '#fff', fontFamily: 'inherit',
};

export function Input({ label, value, onChange, type = 'text', placeholder, options, width, rows }) {
  const common = {
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
    placeholder,
    style: inputStyle,
  };
  return (
    <div style={{ width: width || '100%', minWidth: 0 }}>
      {label && <label style={labelStyle}>{label}</label>}
      {options ? (
        <select {...common}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : rows ? (
        <textarea {...common} rows={rows} />
      ) : (
        <input {...common} type={type} {...(type === 'number' ? { step: 'any' } : {})} />
      )}
    </div>
  );
}

// A row of inputs that lays out sensibly at drawer width.
export function FieldRow({ children }) {
  return <div style={{ display: 'flex', gap: 9, alignItems: 'flex-end' }}>{children}</div>;
}

// ─── Editable list of rows ────────────────────────────────────
//
// Market participants, layers, instalments — all the same shape: a table you
// can add to, edit in place and remove from, with a running total that tells
// you when it does not add up.
//
// Edits are local until saved. A row-by-row PATCH would leave a half-edited
// share panel on the server every time someone changed their mind mid-way.
export function RowEditor({
  columns, rows, onChange, onSave, saving, addLabel = '+ Add row',
  blank, total, footer,
}) {
  const set = (i, key, v) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)));
  };

  // The first column identifies the row — the market's name, the layer's
  // name — so it gets the full width. Everything after it pairs up.
  const [lead, ...rest] = columns;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.length === 0 && (
        <div style={{ fontSize: 12.5, color: C.faint, padding: '4px 0' }}>Nothing yet.</div>
      )}

      {rows.map((r, i) => (
        <div
          key={r._key || r._id || i}
          style={{
            border: `1px solid ${C.border}`, borderRadius: 9,
            padding: '11px 12px', background: '#fff', display: 'grid', gap: 9,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                label={lead.label}
                value={r[lead.key]}
                onChange={(v) => set(i, lead.key, v)}
                type={lead.numeric ? 'number' : 'text'}
                options={lead.options}
                placeholder={lead.placeholder}
              />
            </div>
            <button
              type='button'
              aria-label={`Remove ${r[lead.key] || 'row'}`}
              title='Remove'
              onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
              style={{
                border: `1px solid ${C.border}`, background: '#fff', borderRadius: 7,
                cursor: 'pointer', color: C.faint, padding: '7px 8px', lineHeight: 0,
                flexShrink: 0,
              }}
            >
              {Icons.x(14)}
            </button>
          </div>

          {rest.length > 0 && (
            <div style={{
              display: 'grid', gap: 9,
              gridTemplateColumns: rest.length === 1 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            }}>
              {rest.map(c => (
                <div key={c.key} style={{ minWidth: 0, gridColumn: c.wide ? '1 / -1' : undefined }}>
                  <Input
                    label={c.label}
                    value={r[c.key]}
                    onChange={(v) => set(i, c.key, v)}
                    type={c.numeric ? 'number' : 'text'}
                    options={c.options}
                    placeholder={c.placeholder}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {total && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5,
          paddingTop: 9, borderTop: `1px solid ${C.hair}`,
          color: total.ok ? C.body : C.red, fontWeight: 600,
        }}>
          <span>{total.label}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{total.value}</span>
        </div>
      )}
      {total && !total.ok && total.warning && (
        <div style={{ fontSize: 11.5, color: C.red, lineHeight: 1.5 }}>{total.warning}</div>
      )}
      {footer}

      <ActionRow>
        <ActionButton tone='quiet' onClick={() => onChange([...rows, { ...blank, _key: `n${Date.now()}` }])}>
          {addLabel}
        </ActionButton>
        <ActionButton onClick={onSave} busy={saving}>Save changes</ActionButton>
      </ActionRow>
    </div>
  );
}

// ─── Checklist you can tick ───────────────────────────────────
export function ToggleChecklist({ items = [], onToggle, saving, countLabel }) {
  const done = items.filter(i => i.completed || i.uploaded).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: C.muted }}>
        <span>{countLabel || `${done} of ${items.length} completed`}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
      </div>
      <span style={{ display: 'block', height: 6, borderRadius: 999, background: C.barTrack, overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: C.bar, borderRadius: 999 }} />
      </span>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
        {items.map((item, i) => {
          const on = Boolean(item.completed || item.uploaded);
          return (
            <li key={item.key || i}>
              <button
                type='button'
                onClick={() => onToggle(i, !on)}
                disabled={saving}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                  background: 'none', border: 'none', textAlign: 'left',
                  padding: '6px 4px', borderRadius: 6, cursor: saving ? 'default' : 'pointer',
                  fontSize: 12.5, color: on ? C.body : C.muted, fontFamily: 'inherit',
                }}
              >
                <span style={{
                  width: 16, height: 16, borderRadius: 5, flexShrink: 0,
                  border: `1px solid ${on ? C.green : C.border}`,
                  background: on ? C.greenBg : '#fff', color: C.green,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {on ? Icons.check(11) : null}
                </span>
                <span style={{ textDecoration: on ? 'none' : 'none' }}>{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Editing a record in place ────────────────────────────────
//
// Every module screen could create a record and none could correct one —
// "EDIT OPTION IS NOT PRESENT", on nine of ten modules. A register you can
// only append to is not a register; a mistyped limit stayed mistyped.
//
// One component rather than nine: a screen supplies the fields that are safe
// to change after the fact, and this handles the rest. Fields the server does
// not allow through its writable list simply will not be offered here — the
// allow-list stays the authority, this is only the surface.
export function EditPanel({ record, endpoint, fields = [], onSaved, title = 'Edit' }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const act = useAction(onSaved);

  const start = () => {
    const d = {};
    for (const f of fields) {
      const raw = f.key.split('.').reduce((o, k) => (o == null ? o : o[k]), record);
      d[f.key] = raw == null ? '' : (f.type === 'date' && raw ? String(raw).slice(0, 10) : raw);
    }
    setDraft(d);
    setOpen(true);
  };

  // Only what actually changed is sent. A PATCH that echoes every field back
  // rewrites values the user never touched, and stamps them as edited in the
  // audit trail.
  const save = async () => {
    const body = {};
    for (const f of fields) {
      const before = f.key.split('.').reduce((o, k) => (o == null ? o : o[k]), record);
      const now = draft[f.key];
      const same = String(before ?? '') === String(now ?? '')
        || (f.type === 'date' && String(before ?? '').slice(0, 10) === String(now ?? ''));
      if (same) continue;
      body[f.key] = f.type === 'number' ? (now === '' ? null : Number(now)) : now;
    }
    if (Object.keys(body).length === 0) { setOpen(false); return; }
    const ok = await act.run({
      method: 'patch', url: `${endpoint}/${record._id}`, body,
      success: 'Saved.',
    });
    if (ok) setOpen(false);
  };

  if (!open) return <ActionButton tone='quiet' onClick={start}>{title}</ActionButton>;

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 8, padding: '11px 12px',
      background: C.panel, display: 'grid', gap: 10, width: '100%',
    }}>
      <Notice error={act.error} note={act.note} />
      <FieldRow>
        {fields.map(f => (
          <Input
            key={f.key} label={f.label} type={f.type} options={f.options}
            placeholder={f.placeholder} width={f.width}
            value={draft[f.key]}
            onChange={(v) => setDraft(d => ({ ...d, [f.key]: v }))}
          />
        ))}
      </FieldRow>
      <div style={{ display: 'flex', gap: 8 }}>
        <ActionButton busy={act.saving} onClick={save}>Save changes</ActionButton>
        <ActionButton tone='quiet' onClick={() => setOpen(false)}>Discard</ActionButton>
      </div>
    </div>
  );
}

// ─── Moving a record through its lifecycle ────────────────────
//
// Seven modules offered no way to change a status at all, so a draft stayed a
// draft for ever. The allowed destinations are passed in per screen, because
// what a treaty may become is not what a recovery may become, and a free
// select over the whole enum invites states the workflow never intends.
// `field` because not every resource keeps its state in `status`: a closing
// is a market line on a deal and its state is `closingStatus`. Sending the
// wrong key silently writes nothing — the allow-list drops it and the PATCH
// still returns 200.
export function StatusAction({
  record, endpoint, options = [], onSaved, label = 'Change status', field = 'status',
}) {
  const [to, setTo] = useState('');
  const act = useAction(onSaved);
  const current = record[field] ?? record.status;
  const choices = options.filter(o => o.value !== current);
  if (choices.length === 0) return null;

  return (
    <div style={{ display: 'grid', gap: 8, width: '100%' }}>
      <Notice error={act.error} note={act.note} />
      <FieldRow>
        <Input
          label={label} type='select' value={to} onChange={setTo}
          options={[{ value: '', label: 'Select…' }, ...choices]}
        />
      </FieldRow>
      {to && (
        <ConfirmAction
          label={`Move to ${(choices.find(c => c.value === to) || {}).label || to}`}
          question={`Move ${record.treatyNumber || record.accountNumber || record.recoveryNumber
            || record.renewalNumber || record.counterpartyNumber || record.claimNumber
            || record.dealNumber || 'this record'} to "${
            (choices.find(c => c.value === to) || {}).label || to}"? The change is recorded in the audit trail.`}
          confirmLabel='Confirm'
          busy={act.saving}
          onConfirm={async () => {
            const ok = await act.run({
              method: 'patch', url: `${endpoint}/${record._id}`, body: { [field]: to },
              success: 'Status updated.',
            });
            if (ok) setTo('');
          }}
        />
      )}
    </div>
  );
}

// ─── Taking a record out of the working set ───────────────────
//
// Not a delete. A draft that never left the building is VOIDED; anything
// issued, bound or sent to market is CANCELLED and stays in the register
// saying so. The server decides which from the record's own status — this
// only has to ask for the reason, which it requires, because "who removed
// this and why" is the entire value of keeping the row.
const DRAFTISH = new Set(['draft', 'new', 'pending', 'reviewing', 'early_discussions']);

export function RemoveAction({ record, endpoint, onRemoved, noun = 'record' }) {
  const [reason, setReason] = useState('');
  const act = useAction(onRemoved);
  const isDraft = !record.status || DRAFTISH.has(String(record.status).toLowerCase());
  const verb = isDraft ? 'Void' : 'Cancel';

  return (
    <div style={{ display: 'grid', gap: 8, width: '100%' }}>
      <Notice error={act.error} note={act.note} />
      <Input
        label={`Reason for ${verb.toLowerCase()}ing`} value={reason} onChange={setReason}
        placeholder={isDraft ? 'Raised in error' : 'Cancelled by agreement with the market'}
      />
      <ConfirmAction
        label={`${verb} this ${noun}`}
        tone='danger'
        question={isDraft
          ? `Void this ${noun}? It was never issued, so it will be struck from the register — the record itself is kept, with your reason, and can still be found in reporting.`
          : `Cancel this ${noun}? It has been issued or bound, so it stays in the register marked cancelled. Nothing is deleted.`}
        confirmLabel={`Yes, ${verb.toLowerCase()} it`}
        busy={act.saving}
        onConfirm={() => act.run({
          method: 'delete', url: `${endpoint}/${record._id}`, body: { reason },
          success: `${verb}ed.`,
        })}
      />
    </div>
  );
}

const actions = {
  useAction, Notice, ActionButton, ActionRow, ConfirmAction, Input, FieldRow,
  RowEditor, ToggleChecklist, EditPanel, StatusAction, RemoveAction,
};
export default actions;
